import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useUpdateProductDesignSpec } from '../Product.hooks';
import {
  ProductDesignSpec,
  BaseProductDesignSpecCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';

const DesignSpecInfoCard: React.FC<BaseProductDesignSpecCardProps> = (
  props: BaseProductDesignSpecCardProps
) => {
  const {
    title = `Product ${getProductEntityName('designSpec')} Info`,
    productId,
    productDesignSpec,
    productDesignSpecVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = productDesignSpecVersion || productDesignSpec;
  const initialValues: Partial<ProductDesignSpec> = {
    title: initialData.title || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/design-specs/${productDesignSpec.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductDesignSpec,
    isUpdatingProductDesignSpec: isUpdating,
    updateProductDesignSpecError: updateError,
    updateProductDesignSpec
  } = useUpdateProductDesignSpec(productId, productDesignSpec.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productDesignSpec.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(productDesignSpec.task);
  const error: FetchError | undefined = updateError || updateTaskError;

  useEffect(() => {
    if (updatedProductDesignSpec) {
      onChange?.(updatedProductDesignSpec);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignSpec]);

  useEffect(() => {
    if (updatedTask) {
      productDesignSpec.task = Object.assign(
        productDesignSpec.task || {},
        updatedTask
      );
      onChange?.(productDesignSpec);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <InfoIcon />
        {enableTitleRedirect ? (
          <Link className='link' to={getPath()}>
            {title}
          </Link>
        ) : (
          title
        )}
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={(values: Partial<ProductDesignSpec>) => {
            updateProductDesignSpec(values);
            onChange?.(values);
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='productId'
              label='Product ID'
              value={`PRODUCT-${productId}`}
            />
            <InlineTextField
              name='title'
              label='Title'
              placeholder={`Enter title for Product ${getProductEntityName(
                'designSpec'
              )}`}
              disabled={
                Boolean(productDesignSpecVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText={`Enter description for Product ${getProductEntityName(
                'designSpec'
              )}`}
              disabled={
                Boolean(productDesignSpecVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={productDesignSpec.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productDesignSpec.task) {
                  productDesignSpec.task.attachments = attachments;
                }
                onChange?.(productDesignSpec);
              }}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default DesignSpecInfoCard;
